import dayjs from 'dayjs'
import {DialogDismiss} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {ChevronRightIcon} from '@/components/ui/icon/Icon'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {RadioField} from '@/components/commons/radio-field/RadioField'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useTranslation} from '@/translations/i18n'
import {SiarBulkExportType} from '@/features/projects/siar-bulk-export/types'
import {
    StyledBulkSiarExportDateRow,
    StyledBulkSiarExportDialogBody,
    StyledBulkSiarExportDialogFooter,
    StyledBulkSiarExportRadioGroup
} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/style'

export type BulkExportConfig = {
    exportType: SiarBulkExportType | null
    dateFrom: Date | null
    dateTo: Date | null
    addRegistry: boolean
}

type BulkSiarExportConfigStepProps = {
    config: BulkExportConfig
    onChange: (config: BulkExportConfig) => void
    onNext: () => void
}

export const BulkSiarExportConfigStep = ({config, onChange, onNext}: BulkSiarExportConfigStepProps) => {
    const {t} = useTranslation()
    const {exportType, dateFrom, dateTo, addRegistry} = config

    const isDateRangeInvalid = !!(dateFrom && dateTo && dayjs(dateFrom).isAfter(dayjs(dateTo), 'day'))
    const isFormValid = exportType !== null && (dateFrom !== null || dateTo !== null) && !isDateRangeInvalid

    return (
        <>
            <StyledBulkSiarExportDialogBody>
                <Flexbox direction="column" gap={2}>
                    <Label>{t('projects:bulk_siar_export_type_label')}</Label>
                    <Flexbox align="center" justify="space-between" fullWidth>
                        <StyledBulkSiarExportRadioGroup>
                            <RadioField
                                name="bulk-siar-export-type"
                                value="open_project"
                                checked={exportType === 'open_project'}
                                onChange={() => onChange({...config, exportType: 'open_project'})}
                                label={t('projects:bulk_siar_export_type_open')}
                            />
                            <RadioField
                                name="bulk-siar-export-type"
                                value="close_project"
                                checked={exportType === 'close_project'}
                                onChange={() => onChange({...config, exportType: 'close_project'})}
                                label={t('projects:bulk_siar_export_type_close')}
                            />
                        </StyledBulkSiarExportRadioGroup>
                        <Checkbox
                            id="bulk-siar-export-add-registry"
                            label={t('projects:bulk_siar_export_include_registry')}
                            checked={addRegistry}
                            onChange={() => onChange({...config, addRegistry: !addRegistry})}
                        />
                    </Flexbox>
                </Flexbox>
                <StyledBulkSiarExportDateRow>
                    <InputDate
                        label={t('projects:bulk_siar_export_date_from')}
                        placeholder="gg/mm/aaaa"
                        value={dateFrom}
                        onChange={date => onChange({...config, dateFrom: date})}
                        fullWidth
                        portal
                    />
                    <InputDate
                        label={t('projects:bulk_siar_export_date_to')}
                        placeholder="gg/mm/aaaa"
                        value={dateTo}
                        onChange={date => onChange({...config, dateTo: date})}
                        fullWidth
                        portal
                    />
                </StyledBulkSiarExportDateRow>
                {isDateRangeInvalid && <InputHelpText error={t('projects:bulk_siar_export_date_validation_error')} />}
            </StyledBulkSiarExportDialogBody>
            <StyledBulkSiarExportDialogFooter>
                <DialogDismiss render={<Button variant="secondary">{t('commons:cancel')}</Button>} />
                <Button variant="primaryNeutral" onClick={onNext} disabled={!isFormValid}>
                    {t('projects:bulk_siar_export_preview_button')}
                    <ChevronRightIcon />
                </Button>
            </StyledBulkSiarExportDialogFooter>
        </>
    )
}
